export const EXPERIENCE_COOKIE_NAME = 'NODAFI_WEB_EXPERIENCE'

export type WebExperience = 'legacy' | 'orchid'

/**
 * Long enough that a switch survives a browser restart, short enough that a
 * preference nobody revisits expires instead of outliving the rollout.
 */
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60

const EXPERIENCES: readonly string[] = ['legacy', 'orchid']

/**
 * The cookie CloudFront reads to pick an origin. Host-only — no Domain
 * attribute — because a user can belong to several tenants and be on a
 * different experience in each.
 */
export const buildExperienceCookie = (experience: WebExperience, isSecure: boolean): string => {
  const attributes = [
    `${EXPERIENCE_COOKIE_NAME}=${experience}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ]
  // A Secure cookie is dropped on http://localhost, which would leave the
  // toggle silently inert in local development.
  if (isSecure) attributes.push('Secure')
  return attributes.join('; ')
}

/**
 * Hand this device back to the organization default.
 *
 * A browser removes a cookie only when the removal names the same one: same
 * name, same Path, host-only exactly as written. A near-miss leaves the old
 * cookie in place, still outranking the organization.
 */
export const buildExperienceCookieDeletion = (isSecure: boolean): string => {
  const attributes = [`${EXPERIENCE_COOKIE_NAME}=`, 'Path=/', 'Max-Age=0', 'SameSite=Lax']
  if (isSecure) attributes.push('Secure')
  return attributes.join('; ')
}

export const readExperienceCookie = (cookie: string): WebExperience | null => {
  for (const entry of cookie.split(';')) {
    const separator = entry.indexOf('=')
    if (separator === -1) continue
    // Compare the NAME, not a prefix: `OTHER_NODAFI_WEB_EXPERIENCE` shares our
    // suffix and would otherwise shadow the real cookie sitting behind it.
    if (entry.slice(0, separator).trim() !== EXPERIENCE_COOKIE_NAME) continue
    const value = entry.slice(separator + 1).trim()
    if (EXPERIENCES.includes(value)) return value as WebExperience
  }
  return null
}


/**
 * What to write so this device matches the reader's actual preference, or
 * `null` when it already does.
 *
 * The cookie is a CACHE, not a second source of truth. It exists only because
 * CloudFront picks an origin before any app code runs, with the hostname, the
 * path and the headers to go on — there is no other channel by which a per-user
 * fact reaches that decision.
 *
 * Treating it as authoritative is what made the saved preference inert: a
 * device holding a stale cookie never updated, so enabling the switch on one
 * device changed nothing on any other. Here a disagreement means the cache is
 * STALE, and stale caches get corrected rather than obeyed.
 *
 * An absent cookie is compared as `legacy`, because that is what the edge does
 * with it. That is what keeps this from writing a pointless `legacy` cookie
 * onto a device already being served legacy — a write that would change no
 * routing while inventing a device-local choice the reader never made.
 *
 * `null` on either preference means NOT KNOWN, never `false`. A failed lookup
 * must not read as an opt-out: that would turn every network blip into a silent
 * rollback out of Orchid.
 */
export const experienceToReconcile = ({
  cookie,
  isOrchidChosenByUser,
  isOrchidDefaultForOrg,
}: {
  cookie: string
  isOrchidChosenByUser: boolean | null
  isOrchidDefaultForOrg: boolean | null
}): WebExperience | null => {
  // An explicit reader choice outranks the organization default; neither known
  // means there is nothing to reconcile TO, so the device is left as it is.
  const isOrchidPreferred = isOrchidChosenByUser ?? isOrchidDefaultForOrg
  if (isOrchidPreferred === null) return null

  const wanted: WebExperience = isOrchidPreferred ? 'orchid' : 'legacy'
  const current = readExperienceCookie(cookie) ?? 'legacy'
  return current === wanted ? null : wanted
}

/**
 * Write the saved preference onto this device if it has not chosen one.
 *
 * Separated from the hook that calls it so the side effect is testable: the
 * decision alone is `experienceToReconcile`, and a decision nothing acts on is the
 * shape where a fix looks shipped and changes nothing.
 *
 * Reloads only when it actually wrote a cookie. The edge reads the cookie at
 * request time, so the document already in the browser was served by whichever
 * app the ABSENT cookie selected; without the reload the reader stays there
 * until they navigate, which is the "it did nothing" report.
 *
 * @returns the experience written, or null when it left the device alone.
 */
export const reconcileExperienceCookie = (
  preferences: { isOrchidChosenByUser: boolean | null; isOrchidDefaultForOrg: boolean | null },
  browser: {
    getCookie: () => string
    setCookie: (value: string) => void
    isSecure: boolean
    reload: () => void
  },
  report?: (event: { from: WebExperience; to: WebExperience }) => void,
): WebExperience | null => {
  const cookie = browser.getCookie()
  const experience = experienceToReconcile({ cookie, ...preferences })
  if (experience === null) return null

  // Reported because a correction IS the defect: the reader was in the wrong app
  // until this load moved them, and nothing else can see that. A CloudFront log
  // records what a request carried, and a stranded reader carried no experience
  // cookie — the same as someone who never opted in. Only here are both the
  // running app and the account's preference known at once.
  //
  // Optional, and never awaited: telemetry that can break a page is worse than
  // no telemetry.
  report?.({ from: readExperienceCookie(cookie) ?? 'legacy', to: experience })
  browser.setCookie(buildExperienceCookie(experience, browser.isSecure))
  browser.reload()
  return experience
}


/** Shown when the edge will not serve Orchid on the address the reader is on. */
export const ORCHID_NOT_ROUTED_MESSAGE =
  "The new NodaFi isn't available on this address yet. You're still on the classic app."

/** The `server` header the Orchid worker answers with; anything else is legacy. */
const ORCHID_ROUTER_SERVER = 'cloudflare'

export type RouterProbeResult = {
  isOrchidServed: boolean
  /** What the edge answered, or `error` when the probe never got one. */
  server: string
}

/**
 * Ask the edge which app now serves this origin.
 *
 * The router decides per request from the cookie, so this must run AFTER the
 * cookie is written, and only a real request can answer: a tenant host that is
 * not an alias on the router distribution never sees the cookie at all.
 */
export const probeExperienceRouter = async (): Promise<RouterProbeResult> => {
  try {
    const response = await fetch('/', {
      method: 'HEAD',
      cache: 'no-store',
      credentials: 'include',
    })
    const server = response.headers.get('server')
    // An erroring Orchid worker still answers as `cloudflare`; it is not an app to move onto.
    // Both halves are kept so telemetry can tell which origin was failing.
    if (!response.ok) {
      return { isOrchidServed: false, server: `http_${response.status}_${server ?? 'unknown'}` }
    }
    if (server === null) return { isOrchidServed: false, server: 'unknown' }
    return { isOrchidServed: server.toLowerCase() === ORCHID_ROUTER_SERVER, server }
  } catch {
    // A probe that cannot complete is not evidence the address is routed.
    return { isOrchidServed: false, server: 'error' }
  }
}


export const SWITCH_PENDING_STORAGE_KEY = 'NODAFI_SWITCH_PENDING'

/**
 * Remember, for exactly one load, that a switch is in flight.
 *
 * The edge router picks the app per request, and some addresses are served by
 * one app whatever the cookie says. Which happened is only knowable AFTER the
 * reload, by which app rendered — so the switch records its intent here and the
 * next render answers it. Copying the router's path lists into the app would
 * put a second, drifting copy of that decision in the client.
 */
export const markSwitchPending = (experience: WebExperience): void => {
  try {
    window.sessionStorage.setItem(SWITCH_PENDING_STORAGE_KEY, experience)
  } catch {
    // Storage is unavailable in some privacy modes; a switch must still happen.
  }
}

const readSwitchPending = (): string | null => {
  try {
    return window.sessionStorage.getItem(SWITCH_PENDING_STORAGE_KEY)
  } catch {
    return null
  }
}

const clearSwitchPending = (): void => {
  try {
    window.sessionStorage.removeItem(SWITCH_PENDING_STORAGE_KEY)
  } catch {
    // Nothing to clean up if the store cannot be reached.
  }
}

export type PendingSwitchResult = { outcome: 'not_routed' }

/**
 * Answer a switch that reloaded onto this app anyway.
 *
 * Reaching here with the flag set and an Orchid cookie means the edge serves
 * this address from legacy whatever the cookie says. Sending the reader on to
 * an Orchid-only address only replaced a dead click with a NotFound page, so
 * the cookie comes off instead and the caller undoes the rest.
 *
 * The flag is cleared on every path, acted on or not — a flag that outlives its
 * own load fires again on every later page.
 *
 * @returns the outcome to act on, or null when the reader was left alone.
 */
export const completePendingSwitch = (browser: {
  getCookie: () => string
  setCookie: (value: string) => void
  isSecure: boolean
}): PendingSwitchResult | null => {
  const pending = readSwitchPending()
  clearSwitchPending()
  if (pending !== 'orchid') return null
  // No Orchid cookie means the switch never took, so the reader belongs here.
  if (readExperienceCookie(browser.getCookie()) !== 'orchid') return null

  browser.setCookie(buildExperienceCookieDeletion(browser.isSecure))
  return { outcome: 'not_routed' }
}

type OrchidPreferenceSettings = {
  preferences?: {
    attributes?: {
      isOrchidWebEnabled?: { value?: boolean | null } | null
    } | null
  } | null
}

/**
 * The reader's OWN choice, or null when they have not made one.
 *
 * null means NOT CHOSEN, never `false`: an explicit opt-out outranks the
 * organization default, and only an explicit choice can be handed back.
 */
export const readUserOrchidChoice = (
  settings: OrchidPreferenceSettings | null | undefined,
): boolean | null => settings?.preferences?.attributes?.isOrchidWebEnabled?.value ?? null

/** What the reader is on, and whether it is their own doing. */
export const experienceChoiceLabel = (isOrchidChosenByUser: boolean | null): string => {
  if (isOrchidChosenByUser === null) return "Following your organization's default"
  return isOrchidChosenByUser ? 'Using the new NodaFi' : 'Using classic NodaFi'
}
